import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  AreaChart as RechartAreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Area,
  Tooltip as RechartTooltip,
} from 'recharts';
import { AxisInterval } from 'recharts/types/util/types';
import { theme } from '@frontend/theme';
import { EmptyData } from '../atoms/empty-data';
import { renderHorizontalMarker } from '../atoms/horizontal-marker';
import { ScrollControl } from '../atoms/scroll-control';
import { Tooltip } from '../atoms/tooltip';
import { BarClick } from '../bar-chart/types';
import { useChartContext } from '../chart.provider';
import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { defaultFormatter, generateLegends, generateTooltipData } from '../helpers/chart-utils';
import { showEmptyStateForBarAndAreaChart } from '../helpers/show-empty-state-calc';
import { useIsVisible } from '../hooks/use-is-visible';
import { useScrollControls } from '../hooks/use-scroll-controls';
import { FormatValue } from '../types';
import { ActiveDot } from './active-dot';
import { generateGradientDefs } from './gradient-area';
import { Appearance, AreaChartData } from './types';
import { YAxisTick } from './y-axis-tick';

type MultiAxisClubIds = Record<string, string>;

type Props = {
  appearance?: Appearance;
  customLegendsIds?: string[];
  data?: AreaChartData;
  formatValue?: FormatValue | Record<string, FormatValue>;
  onClick?: (args: BarClick) => void;

  // This prop is used to club multiple lines into single axis for multi-unit (nomral value, currency etc) type charts
  // The lines that needs to be clubbed should have the same id
  multiAxisClubIds?: MultiAxisClubIds;
};

const getMaxLineValues = (data: AreaChartData, multiAxisClubIds?: MultiAxisClubIds) => {
  const maxValues = data.groups?.reduce((acc, { values }) => {
    const keys = Object.keys(values);
    return {
      ...acc,
      ...keys.reduce((acc2, key) => {
        const axisKey = multiAxisClubIds?.[key] || key;
        const max = Math.max(acc[axisKey] || 0, acc2[axisKey] || 0, values[key]);
        acc2[axisKey] = max;
        return acc2;
      }, {} as Record<string, number>),
    };
  }, {} as Record<string, number>);

  return maxValues;
};

const normalizeValue = (value: number, maxValue: number, maxRenderValue: number) => (value / maxValue) * maxRenderValue;

const denormalizeValue = (normalizedValue: number, maxValue: number, maxRenderValue: number) =>
  (normalizedValue / maxRenderValue) * maxValue;

const scrollerPadding = 10;
// Keeping a minimum of top margin to avoid clipping of the animated active dot
const defaultMargin = (showYAxis?: boolean, showScrollControls?: boolean) => ({
  top: 12,
  right: 40,
  bottom: 0,
  left: showYAxis && showScrollControls ? 20 : 0,
});
const lineStrokeWidth = 3;

const axisLine = {
  stroke: theme.colors.neutral10,
  strokeDasharray: '1 1',
  strokeWidth: 1,
};

export const AreaChart = memo(
  ({ appearance = {}, customLegendsIds, data = { groups: [] }, formatValue, multiAxisClubIds, onClick }: Props) => {
    const {
      collectiveTooltip,
      customTooltipData,
      customTooltipTitle,
      customXAxisTick,
      customXAxisTickFormat,
      customYAxisTick,
      customYAxisTickFormat,
      gradientOpacity,
      height = 240,
      hideTooltip,
      margin,
      maxValue,
      showGridLines,
      showScrollControls = true,
      showXAxis,
      showYAxis,
      tooltipBottomContentRenderer,
      tooltipLabelsTransform,
      width = '100%',
      wrapperStyles,
      xAxisTickWidth = 80,
    } = appearance;
    const { activeLegends, colors, commonTooltipLabel, emptyStateConfig, labels, setPrimaryLegendsList } =
      useChartContext();
    const scrollableWrapperRef = useRef<HTMLDivElement>(null);
    const maxValueRef = useRef<number>(0);
    const maxRenderValueRef = useRef<number>(0);
    const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const isChartVisible = useIsVisible(scrollableWrapperRef);

    const updatedMargin = { ...defaultMargin(showYAxis, showScrollControls), ...margin };

    const groupsCount = data.groups?.length;
    // Do not use minChartSize for any calculations, it is just for the chart container to have a minimum width
    const minChartSize =
      groupsCount * xAxisTickWidth +
      // For vertical layout, when y-axis is visible add additional 60px (approx value of inbuilt y-axis width)
      updatedMargin.left +
      updatedMargin.right +
      (showYAxis ? 60 : 0);

    const customValueTickBase = maxValue ? maxValue / 4 : undefined;
    const customValueTicks =
      maxValue && customValueTickBase
        ? [0, customValueTickBase, customValueTickBase * 2, customValueTickBase * 3, maxValue]
        : undefined;

    const showEmptyState = useMemo(() => {
      return showEmptyStateForBarAndAreaChart({
        ...emptyStateConfig,
        data,
      });
    }, [data, emptyStateConfig]);

    // These unique formatters are used to render multi units in the value axis
    const collectedUniqueFormatters = useMemo(() => {
      if (!formatValue || typeof formatValue === 'function') {
        if (!formatValue) {
          return [defaultFormatter];
        } else {
          return [formatValue];
        }
      } else {
        // Two keys might same same formatter, so we need to collect unique formatters
        return Object.values(formatValue).reduce((acc, formatter) => {
          if (!acc.includes(formatter)) {
            acc.push(formatter);
          }
          return acc;
        }, [] as FormatValue[]);
      }
    }, [formatValue]);

    const chartData = useMemo(() => {
      const groupedData: {
        name: string;
        [key: string]: number | string;
      }[] = [];

      // multiAxisClubIds is expected to be present when multiple types of data (eg. days count, production value etc) being shown in the same chart
      // In such cases, we need to normalize the values based on the max value of the respective axis
      if (multiAxisClubIds) {
        const maxLineValues = getMaxLineValues(data, multiAxisClubIds);
        const maxValuesArray = Object.values(maxLineValues);
        maxValueRef.current = Math.max(...maxValuesArray);
        maxRenderValueRef.current = Math.min(...maxValuesArray);

        data?.groups?.forEach(({ name, values }) => {
          const group = {
            name,
            ...Object.entries(values).reduce((acc, [id, value]) => {
              acc[id] = normalizeValue(value, maxLineValues[multiAxisClubIds?.[id] || id], maxRenderValueRef.current);
              return acc;
            }, {} as Record<string, number>),
          };
          groupedData.push(group);
        });
      } else {
        data?.groups?.forEach(({ name, values }) => {
          const group = { name, ...values };
          groupedData.push(group);
        });
      }

      return groupedData;
    }, [data, multiAxisClubIds]);

    const legendsAndTooltipData = useMemo(() => {
      if (!data?.groups?.length) {
        return;
      }

      return {
        legends: generateLegends(data),
        tooltipData: generateTooltipData({ colors, commonTooltipLabel, customTooltipData, data, formatValue, labels }),
      };
    }, [data]);

    const { isLeftScrollDisabled, isRightScrollDisabled, isScrollable, resetScroll, scrollLeft, scrollRight } =
      useScrollControls(
        {
          containerRef: scrollableWrapperRef,
        },
        [chartData, isChartVisible, showEmptyState, showScrollControls]
      );

    useEffect(() => {
      const legends = customLegendsIds ?? legendsAndTooltipData?.legends;
      setPrimaryLegendsList(legends ?? []);
    }, [legendsAndTooltipData?.legends]);

    useEffect(() => {
      resetScroll();
    }, [chartData, showEmptyState, resetScroll]);

    useEffect(() => {
      // When scrolling, close the tooltip
      const handleScroll = () => {
        setHoveredGroup(null);
      };

      scrollableWrapperRef.current?.addEventListener('scroll', handleScroll);

      return () => {
        scrollableWrapperRef.current?.removeEventListener('scroll', handleScroll);
      };
    }, [scrollableWrapperRef]);

    const handleAreaClick = (legend: string) => {
      if (!onClick) {
        return undefined;
      }

      return () => {
        if (!hoveredGroup) {
          return;
        }

        const selectedGroup = chartData.find((group) => group.name === hoveredGroup);
        if (!selectedGroup) {
          return;
        }

        onClick({
          barId: legend,
          group: selectedGroup.name,
          value: Number(selectedGroup[legend] ?? 0),
        });
      };
    };

    return (
      <div css={styles.mainParent}>
        <div
          css={[styles.scrollableWrapper, wrapperStyles]}
          ref={scrollableWrapperRef}
          style={{
            paddingBottom: isScrollable ? scrollerPadding : 0,
          }}
        >
          <div
            className='pdf-chart-view'
            style={{
              height,
              minHeight: showEmptyState ? EMPTY_STATE_MIN_HEIGHT : height,
              minWidth: minChartSize,
              width,
            }}
          >
            {!showEmptyState ? (
              <ResponsiveContainer width='100%' height='100%' key={JSON.stringify(data.groups || {})}>
                <RechartAreaChart
                  data={chartData}
                  margin={updatedMargin}
                  onMouseLeave={() => {
                    setHoveredGroup(null);
                  }}
                  onMouseMove={(state, event) => {
                    if (!hideTooltip) {
                      setHoveredGroup(state.activeLabel ?? null);
                      setTooltipPosition({
                        x: event.clientX,
                        y: event.clientY,
                      });
                    }
                  }}
                  style={{
                    cursor: onClick ? 'pointer' : 'default',
                  }}
                >
                  {showGridLines && <CartesianGrid stroke={theme.colors.neutral10} strokeDasharray='1 1' />}

                  <XAxis
                    axisLine={axisLine}
                    dataKey='name'
                    dy={8}
                    fontSize={14}
                    hide={!showXAxis}
                    interval={'preserveStartEnd' as AxisInterval}
                    tick={
                      customXAxisTick
                        ? (props) => {
                            return customXAxisTick?.({
                              groupName: props.payload.value,
                              isHovered: props.payload.value === hoveredGroup,
                              labels,
                              x: props.x,
                              y: props.y,
                            });
                          }
                        : undefined
                    }
                    tickFormatter={customXAxisTickFormat}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={axisLine}
                    domain={maxValue ? [0, maxValue] : undefined}
                    fontSize={14}
                    hide={!showYAxis}
                    tickCount={5}
                    tickFormatter={customYAxisTickFormat}
                    tick={
                      customYAxisTick
                        ? (props) => {
                            return customYAxisTick?.({
                              groupName: props.payload.value,
                              isHovered: props.payload.value === hoveredGroup,
                              labels,
                              x: props.x,
                              y: props.y,
                            });
                          }
                        : multiAxisClubIds
                        ? (props) => (
                            <YAxisTick
                              {...props}
                              formatters={collectedUniqueFormatters}
                              subValue={(value) =>
                                denormalizeValue(value, maxValueRef.current, maxRenderValueRef.current)
                              }
                            />
                          )
                        : undefined
                    }
                    tickLine={false}
                    ticks={customValueTicks}
                  />

                  {renderHorizontalMarker(data.markers, colors)}

                  <RechartTooltip
                    content={() => {
                      // We have custom tooltip, so we don't need the default one
                      // But we still need to use rechart tooltip to render the cursor
                      return null;
                    }}
                    cursor={{
                      fill: theme.colors.neutral20,
                      strokeDasharray: '10 3',
                      strokeWidth: 2,
                    }}
                  />

                  {activeLegends.map((legend) => {
                    const color = colors[legend] || theme.colors.neutral50;
                    const defId = `def-${legend.replace(/[^a-zA-Z0-9]/g, '')}`;

                    return (
                      <Fragment key={legend}>
                        {generateGradientDefs(defId, color, gradientOpacity?.[legend])}
                        <Area
                          activeDot={<ActiveDot color={color} size={6} />}
                          dataKey={legend}
                          fill={`url(#${defId})`}
                          onClick={handleAreaClick(legend)}
                          stroke={color}
                          strokeWidth={lineStrokeWidth}
                          type='monotone'
                        />
                      </Fragment>
                    );
                  })}
                </RechartAreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyData emptyStateConfig={emptyStateConfig} />
            )}
          </div>
          {hoveredGroup && !hideTooltip && (
            <Tooltip
              bottomContentRenderer={tooltipBottomContentRenderer}
              data={legendsAndTooltipData?.tooltipData[hoveredGroup] || []}
              labelsTransform={tooltipLabelsTransform}
              name={
                customTooltipTitle?.({
                  groupName: hoveredGroup,
                }) || hoveredGroup
              }
              skipActiveLegendsCheck={collectiveTooltip}
              xPos={tooltipPosition.x}
              yPos={tooltipPosition.y}
            />
          )}
        </div>

        {showScrollControls && isScrollable && (
          <>
            <ScrollControl absolute direction='left' disabled={isLeftScrollDisabled} onClick={scrollLeft} />
            <ScrollControl absolute direction='right' disabled={isRightScrollDisabled} onClick={scrollRight} />
          </>
        )}
      </div>
    );
  }
);

AreaChart.displayName = 'AreaChart';

const styles = {
  scrollableWrapper: css`
    overflow: auto;
    position: relative;
  `,

  mainParent: css`
    max-width: 100%;
    position: relative;
  `,
};
